import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

export default function Login() {
  const navigate = useNavigate();
  const { login, signup } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await signup(form.email, form.password, form.name);
      toast.success("Welcome back.");
      navigate("/admin");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#F8F8F7]" data-testid="login-page">
      <div className="hidden md:block relative overflow-hidden">
        <img src="https://images.unsplash.com/photo-1603805752838-aa579d77da72?crop=entropy&cs=srgb&fm=jpg&ixid=M3w8NjY2NzV8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwZmFzaGlvbiUyMGFjY2Vzc29yaWVzJTIwZmxhdGxheXxlbnwwfHx8fDE3ODIwNDU3MTl8MA&ixlib=rb-4.1.0&q=85" alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/30" />
        <div className="absolute top-10 left-10 right-10">
          <Link to="/" className="text-white font-display font-black text-2xl tracking-tighter" data-testid="brand-link">CURATED&nbsp;/&nbsp;FOUR</Link>
        </div>
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <p className="overline opacity-80">Operator console</p>
          <h2 className="font-display font-black text-5xl tracking-tighter mt-3 leading-none">Manage four<br />platforms<br />from one room.</h2>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 md:p-16">
        <div className="w-full max-w-md">
          <p className="overline text-[#666]">{mode === "login" ? "Welcome back" : "Create your account"}</p>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mt-3">
            {mode === "login" ? "Sign in." : "Sign up."}
          </h1>

          <form onSubmit={submit} className="mt-10 space-y-5">
            {mode === "signup" && (
              <Field label="Name" testid="name-input">
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full bg-transparent border-b border-black/30 py-3 outline-none focus:border-[#E53935] transition-colors"
                />
              </Field>
            )}
            <Field label="Email" testid="email-input">
              <input
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full bg-transparent border-b border-black/30 py-3 outline-none focus:border-[#E53935] transition-colors"
              />
            </Field>
            <Field label="Password" testid="password-input">
              <input
                required
                type="password"
                minLength={6}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full bg-transparent border-b border-black/30 py-3 outline-none focus:border-[#E53935] transition-colors"
              />
            </Field>

            <button
              data-testid="auth-submit"
              type="submit"
              disabled={loading}
              className="w-full mt-4 bg-[#0A0A0A] text-white py-4 uppercase tracking-wider text-sm font-semibold hover:bg-[#E53935] transition-colors disabled:opacity-50"
            >
              {loading ? "..." : mode === "login" ? "Sign In" : "Create Account"}
            </button>
          </form>

          <button
            data-testid="auth-toggle"
            type="button"
            onClick={() => setMode(mode === "login" ? "signup" : "login")}
            className="mt-8 text-sm story-link"
          >
            {mode === "login" ? "No account? Sign up →" : "Already registered? Sign in →"}
          </button>

          <div className="mt-10">
            <Link to="/" className="text-xs uppercase tracking-wider text-[#666] story-link">← Back to store</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, testid }) {
  return (
    <div data-testid={testid}>
      <label className="overline text-[#666]">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
